import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Mail, Save, Loader2, Bell, AlertTriangle } from "lucide-react";

const EmailNotificationCard = React.memo(function EmailNotificationCard({
  ccRemindersToTeamLeader,
  setCcRemindersToTeamLeader,
  ccRemindersToAdmin,
  setCcRemindersToAdmin,
  ccRemindersToTaj,
  setCcRemindersToTaj,
  notifyTeamLeaderOnSubmission,
  setNotifyTeamLeaderOnSubmission,
  notifyAdminOnSubmission,
  setNotifyAdminOnSubmission,
  notifyTajOnSubmission,
  setNotifyTajOnSubmission,
  productivityThreshold,
  setProductivityThreshold,
  productivityAlertEmail,
  setProductivityAlertEmail,
  savingNotifications,
  handleSaveNotifications,
}) {
  return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-slate-600" />
            <CardTitle>Email Notification Settings</CardTitle>
          </div>
          <CardDescription>
            Configure who receives email notifications for reminders and log submissions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* CC Reminders */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">CC Reminder Emails To:</Label>
              <p className="text-sm text-slate-500 mb-3">
                When sending reminder emails to users, also CC the following recipients
              </p>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cc-team-leader"
                      checked={ccRemindersToTeamLeader}
                      onCheckedChange={setCcRemindersToTeamLeader}
                    />
                    <Label htmlFor="cc-team-leader" className="text-sm font-normal cursor-pointer">
                      Manager (Reports To)
                    </Label>
                  </div>
                  <p className="text-xs text-slate-400 ml-6">Only the user&apos;s direct manager(s) set via &quot;Reports To&quot; in Edit User</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cc-admin"
                      checked={ccRemindersToAdmin}
                      onCheckedChange={setCcRemindersToAdmin}
                    />
                    <Label htmlFor="cc-admin" className="text-sm font-normal cursor-pointer">
                      Admin
                    </Label>
                  </div>
                  <p className="text-xs text-slate-400 ml-6">Only admins who share a team with the user</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cc-taj"
                    checked={ccRemindersToTaj}
                    onCheckedChange={setCcRemindersToTaj}
                  />
                  <Label htmlFor="cc-taj" className="text-sm font-normal cursor-pointer">
                    taj@jobtalk.ai
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <hr className="border-slate-200" />

          {/* Log Submission Notifications */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Receive Email When User Submits Daily Log:
              </Label>
              <p className="text-sm text-slate-500 mb-3">
                Send notification emails when a user submits their daily log
              </p>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="notify-team-leader"
                      checked={notifyTeamLeaderOnSubmission}
                      onCheckedChange={setNotifyTeamLeaderOnSubmission}
                    />
                    <Label htmlFor="notify-team-leader" className="text-sm font-normal cursor-pointer">
                      Manager (Reports To)
                    </Label>
                  </div>
                  <p className="text-xs text-slate-400 ml-6">Only the user&apos;s direct manager(s) set via &quot;Reports To&quot; in Edit User</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="notify-admin"
                      checked={notifyAdminOnSubmission}
                      onCheckedChange={setNotifyAdminOnSubmission}
                    />
                    <Label htmlFor="notify-admin" className="text-sm font-normal cursor-pointer">
                      Admin
                    </Label>
                  </div>
                  <p className="text-xs text-slate-400 ml-6">Only admins who share a team with the user</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="notify-taj"
                    checked={notifyTajOnSubmission}
                    onCheckedChange={setNotifyTajOnSubmission}
                  />
                  <Label htmlFor="notify-taj" className="text-sm font-normal cursor-pointer">
                    taj@jobtalk.ai
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong>
            </p>
            <p className="text-sm text-blue-700">
              <strong>Manager:</strong> Uses the &quot;Reports To&quot; field in Edit User. A user can report to multiple managers.
            </p>
            <p className="text-sm text-blue-700">
              <strong>Admin:</strong> Only admins assigned to the same team(s) as the user receive notifications.
            </p>
          </div>

          {/* Satisfaction # / Productivity Monitoring */}
          <div className="pt-4 border-t space-y-4">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Satisfaction # (Productivity Monitoring)
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="productivity-threshold">Satisfaction #</Label>
                <Input
                  id="productivity-threshold"
                  type="number"
                  min="1"
                  max="10"
                  placeholder="e.g., 4"
                  value={productivityThreshold}
                  onChange={(e) => setProductivityThreshold(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  If productivity rating is below this number, user will be asked &quot;What can leadership team do to help?&quot; and an alert email will be sent. Leave empty to disable.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="productivity-alert-email">Alert Email Address</Label>
                <Input
                  id="productivity-alert-email"
                  type="email"
                  placeholder="taj@jobtalk.ai"
                  value={productivityAlertEmail}
                  onChange={(e) => setProductivityAlertEmail(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Email to receive low satisfaction alerts
                </p>
              </div>
            </div>

            {productivityThreshold && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>&#x26A0;&#xFE0F; Active:</strong> When any user rates productivity below <strong>{productivityThreshold}</strong>, they will be prompted for leadership feedback and an alert will be sent to <strong>{productivityAlertEmail}</strong>
                </p>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4 flex justify-end">
            <Button onClick={handleSaveNotifications} disabled={savingNotifications}>
              {savingNotifications ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Notification Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
  );
});

export default EmailNotificationCard;
